import type { TimeEntryGroup, TimeEntryRow } from "./groupTimeEntries";

const TECHNICIAN_GROUP_NAME = "technician";
const APPROVED_TICKET_STATUS = "DONE";

type CanEditTimeEntryArgs = {
  sessionId: string | null;
  ticketStatus?: string | null;
  assigneeIds: number[];
  currentUserId?: number;
  currentUserGroups?: string[];
};

/**
 * UX-layer visibility rule for the time entry edit control. The backend guard in
 * time_tracking/views.py is the real enforcement; this only decides whether the
 * control is shown, and mirrors that guard exactly.
 *
 * Staff (managers, coordinators, administrators) may edit any entry. Technicians may
 * edit only cohorts they belong to. Nobody may edit once the work order is approved,
 * or edit a legacy row that predates session_id and therefore has no cohort.
 */
export function canEditTimeEntry({
  sessionId,
  ticketStatus,
  assigneeIds,
  currentUserId,
  currentUserGroups,
}: CanEditTimeEntryArgs): boolean {
  if (currentUserId == null || sessionId == null) {
    return false;
  }
  if (ticketStatus === APPROVED_TICKET_STATUS) {
    return false;
  }
  const isTechnician = (currentUserGroups ?? []).some(
    (group) => group.toLowerCase() === TECHNICIAN_GROUP_NAME
  );
  if (!isTechnician) {
    return true;
  }
  return assigneeIds.includes(currentUserId);
}

/**
 * The edit is addressed to one row and applied to its whole cohort. A technician has
 * to address their own row, or the backend rejects it as not_time_entry_owner.
 */
export function editableRowId<Row extends TimeEntryRow>(
  group: TimeEntryGroup<Row>,
  currentUserId?: number
): string | undefined {
  const ownRow = group.rows.find((row) => row.assignee === currentUserId);
  return ownRow?.id ?? group.rows[0]?.id;
}
